/**
 * Zone + quest gating (Phase 3). Pure derivations over PlayerState — the
 * Combat page, the Quests page, and the store guards all read the SAME
 * rules, so the UI can never disagree with what the store will allow.
 *
 * Rules:
 *   - the Codex Quarry roster is sealed until `roaches_in_tmp` is complete
 *     (flag `codex_quarry`, or the completed quest itself for older saves);
 *   - before that, only the Free-Disk Roach pantry drill is fightable;
 *   - `ship_it` cannot be accepted until the zone is unlocked;
 *   - the Parakeet requires `ship_it` active or complete.
 */
import { QUESTS_BY_ID } from '../../data';
import type { MonsterId, PlayerState } from '../state';
import { hasFlag, isQuestActive, isQuestComplete, questEntry } from './engine';

export const ZONE_UNLOCK_FLAG = 'codex_quarry';
export const TUTORIAL_QUEST_ID = 'roaches_in_tmp';
export const BOSS_QUEST_ID = 'ship_it';
export const BOSS_MONSTER_ID: MonsterId = 'mr_sparks_parakeet';

export function isZoneUnlocked(state: PlayerState): boolean {
  return hasFlag(state, ZONE_UNLOCK_FLAG) || isQuestComplete(state, TUTORIAL_QUEST_ID);
}

export type Availability = { available: true } | { available: false; reason: string };

export function monsterAvailability(state: PlayerState, monsterId: MonsterId): Availability {
  if (monsterId === BOSS_MONSTER_ID) {
    if (!isZoneUnlocked(state)) {
      return { available: false, reason: 'Sealed until /tmp is clean. Complete Roaches in /tmp.' };
    }
    if (!isQuestActive(state, BOSS_QUEST_ID) && !isQuestComplete(state, BOSS_QUEST_ID)) {
      return { available: false, reason: 'Crown commission required. Accept Ship It first.' };
    }
    return { available: true };
  }
  if (monsterId === 'free_disk_roach' || isZoneUnlocked(state)) return { available: true };
  return { available: false, reason: 'Sealed until /tmp is clean. Complete Roaches in /tmp.' };
}

export type AcceptCheck = { ok: true } | { ok: false; reason: string };

export function canAcceptQuest(state: PlayerState, questId: string): AcceptCheck {
  const quest = QUESTS_BY_ID.get(questId);
  if (!quest) return { ok: false, reason: 'Unknown quest.' };
  const held = questEntry(state, questId);
  if (held) {
    return {
      ok: false,
      reason: held.status === 'complete' ? 'Already completed.' : 'Already accepted.',
    };
  }
  if ((questId === BOSS_QUEST_ID || questId === 'first_green') && !isZoneUnlocked(state)) {
    return { ok: false, reason: 'Unavailable until the Codex Quarry is unlocked.' };
  }
  return { ok: true };
}
